import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  recordImpressionByName,
  sendEvent,
} from '@JohnLewisPartnership/experiments-utils';
import { dispatchNewEvent } from '../../utils/analytics/dispatchNewEvent';
import { getTopLinksWithMenu } from '../links';
import { Prenav } from '../ui/prenav/Prenav';
import { Logo } from '../logo/Logo';
import { UserTools } from '../user-tools/UserTools';
import { Search } from '../search/Search';
import {
  min1024pxWideMediaQuery,
  isMin1024pxWide,
  isMin768pxWide,
} from '../../utils/device/identifyDevice';
import { isMonetateGroup } from '../../utils/monetate/isMonetateGroup';
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../scaffold/UserContext';

import styles from './responsive-header.scss';
import { TypeaheadOverlay } from '../typeahead-overlay/TypeaheadOverlay';
import { CloseButton } from '../typeahead-overlay/CloseButton';
import BackButton from '../typeahead-overlay/BackButton';

const peepHideThresholdPx = 150;
const peepShowThresholdPx = 100;

export const ResponsiveHeader = ({
  updateMobileNavState,
  updateTypeaheadState,
}) => {
  const {
    features,
    envVars,
    stickySearchHeader: hasStickyHeader,
  } = useContext(EnvVarsFeaturesContext);

  const {
    userState: {
      user: { monetateId },
    },
  } = useContext(UserContext);

  const [state, setState] = useState({
    isFocused: false,
    isVisible: true,
    previousDirectionChangePx: 0,
    previousScrollPositionPx: 0,
    showNav: false,
    stickyHeaderActive: false,
    wasScrollingUp: false,
  });

  const { onlyPeepAtTop = false } = features;

  const monetate = envVars.monetate;

  const scrollbarWidthRef = useRef(0);
  const header = useRef(null);
  const headerContainer = useRef(null);
  const scrollPositionPxRef = useRef(0);
  const directionChangePxRef = useRef(0);
  const wasScrollingUpRef = useRef(false);
  const stickyHeaderActiveRef = useRef(false);
  const isFocusedRef = useRef(false);
  const isVisibleRef = useRef(true);
  const showNavRef = useRef(false);

  const shouldRecordImpression = isMonetateGroup(features, 'record-impression');
  const isWhiteTypeaheadActive = true;
  const topLinksData = getTopLinksWithMenu(envVars?.accountBaseUrl);

  const escKeyHandler = event => {
    if (event.keyCode === 27) {
      document.activeElement.blur();
      searchInputBlur();
    }
  };

  const handleDeviceOrientation = useCallback(() => {
    if (isMin1024pxWide()) {
      updateMobileNavState(false);
      window.document.body.classList.remove('lockBody');
    }
    setState(prevState => ({ ...prevState, showNav: false }));
    showNavRef.current = false;
  }, [updateMobileNavState]);

  const handleUnpeep = () => {
    if (!isMin1024pxWide() && window.pageYOffset >= peepHideThresholdPx) {
      dispatchPeepEvent(false);
      setState(prevState => ({ ...prevState, isVisible: false }));
    }
  };

  const onMenuButtonClick = useCallback(
    (e = null, forceClose = false) => {
      let showNav;

      if (forceClose) {
        showNav = false;
      } else {
        showNav = !showNavRef.current;
      }

      if (e) e.preventDefault();

      const currentShowNav = showNavRef.current;
      dispatchNewEvent('dataStateChange', {
        action: 'topNav - Side Flyout',
        type: currentShowNav ? 'Closed' : 'Open',
      });
      if (currentShowNav) {
        sendEvent('mobile-menu-click.menu-close');
      }

      setState(prevState => ({ ...prevState, showNav }));
      showNavRef.current = showNav;
      updateMobileNavState(showNav);

      const menuClick = new CustomEvent(
        'jl-events.browser.mobileHeaderMenuClick',
        {
          detail: {
            hide: showNav,
          },
        }
      );

      window.dispatchEvent(menuClick);

      if (showNav) {
        window.document.body.classList.add('lockBody');
      } else {
        window.document.body.classList.remove('lockBody');
      }
    },
    [updateMobileNavState]
  );

  const searchInputBlur = useCallback(() => {
    setState(prevState => ({ ...prevState, isFocused: false }));
    isFocusedRef.current = false;
    window.removeEventListener('keydown', escKeyHandler);

    if (isMin768pxWide()) {
      setState(prevState => ({ ...prevState, stickyHeaderActive: false }));
      stickyHeaderActiveRef.current = false;
    }

    // Old styling - only apply to desktop
    if (isMin1024pxWide()) {
      document.body.style.paddingRight = '';
      document.body.classList.remove(styles.typeaheadOpen);
    } else {
      window.scrollTo(0, 0);
    }
  }, [escKeyHandler]);

  const onSearchTabOut = useCallback(
    e => {
      if (e?.relatedTarget?.id.includes('desktop-menu-item')) {
        searchInputBlur();
      }
    },
    [searchInputBlur]
  );

  const setVisibility = () => {
    if (isMin1024pxWide() && !hasStickyHeader) {
      if (!isVisibleRef.current) {
        setState(prevState => ({ ...prevState, isVisible: true }));
        isVisibleRef.current = true;
      }
      return;
    }

    const currentScrollPositionPx = window.pageYOffset;
    const previousScrollPositionPx = scrollPositionPxRef.current;
    const previousDirectionChangePx = directionChangePxRef.current;

    const isCurrentlyScrollingUp =
      currentScrollPositionPx < previousScrollPositionPx;

    const newVisibilityState = {};

    const scrollDirectionDidChange = () =>
      wasScrollingUpRef.current !== isCurrentlyScrollingUp;

    const scrollPositionOffTopOfPage = () => currentScrollPositionPx < 0;

    const headerAtTopOfScreen = () =>
      currentScrollPositionPx < peepHideThresholdPx;

    const updatePreviousDirectionChangePx = () => {
      // Record pixel position at which scrolling direction last reversed
      if (scrollDirectionDidChange()) {
        return scrollPositionOffTopOfPage() || showNavRef.current
          ? previousDirectionChangePx
          : currentScrollPositionPx;
      }
      return previousDirectionChangePx;
    };

    const scrolledUpPastPeepThreshold = () => {
      const showThresholdPx = previousDirectionChangePx - peepShowThresholdPx;

      let showOnScrollUp = isCurrentlyScrollingUp;
      if (onlyPeepAtTop) {
        showOnScrollUp = false;
      }

      return (
        !isVisibleRef.current &&
        showOnScrollUp &&
        currentScrollPositionPx < showThresholdPx
      );
    };

    const scrolledDownPastPeepThreshold = () => {
      const hideThresholdPx = previousDirectionChangePx + peepHideThresholdPx;

      return (
        isVisibleRef.current &&
        !isCurrentlyScrollingUp &&
        currentScrollPositionPx > hideThresholdPx
      );
    };

    const showHeader = () => {
      newVisibilityState.isVisible = true;
      dispatchPeepEvent(true);
    };

    const hideHeader = () => {
      newVisibilityState.isVisible = false;
      dispatchPeepEvent(false);
    };

    const showStickyHeader = () => {
      newVisibilityState.stickyHeaderActive = true;
      if (!stickyHeaderActiveRef.current) {
        dispatchStickyHeaderEvent(true);
      }
    };

    const hideStickyHeader = () => {
      newVisibilityState.stickyHeaderActive = false;
      if (stickyHeaderActiveRef.current) {
        dispatchStickyHeaderEvent(false);
      }
    };

    if (isMin768pxWide() && hasStickyHeader) {
      if (scrolledDownPastPeepThreshold()) {
        showStickyHeader();
      }

      if (headerAtTopOfScreen()) {
        hideStickyHeader();
      }
    } else {
      if (scrolledUpPastPeepThreshold() || headerAtTopOfScreen()) {
        showHeader();
      }

      if (scrolledDownPastPeepThreshold()) {
        hideHeader();
      }
    }

    const newDirectionChangePx = updatePreviousDirectionChangePx();
    const newScrollPositionPx =
      currentScrollPositionPx < 0 ? 0 : currentScrollPositionPx;

    directionChangePxRef.current = newDirectionChangePx;
    scrollPositionPxRef.current = newScrollPositionPx;

    wasScrollingUpRef.current = isCurrentlyScrollingUp;

    if ('stickyHeaderActive' in newVisibilityState) {
      stickyHeaderActiveRef.current = newVisibilityState.stickyHeaderActive;
    }
    if ('isVisible' in newVisibilityState) {
      isVisibleRef.current = newVisibilityState.isVisible;
    }

    setState(prevState => ({
      ...prevState,
      ...newVisibilityState,
      previousDirectionChangePx: newDirectionChangePx,
      wasScrollingUp: isCurrentlyScrollingUp,
      previousScrollPositionPx: newScrollPositionPx,
    }));
  };

  const updateView = useCallback(() => {
    if (!isFocusedRef.current) {
      requestAnimationFrame(() => {
        setVisibility();
      });
    }
  }, []);

  useEffect(function setScrollbarWidth() {
    scrollbarWidthRef.current = window.innerWidth - document.body.clientWidth;
  }, []);

  useEffect(function listenForScroll() {
    updateView();
    window.addEventListener('scroll', updateView);

    return function stopListeningForScroll() {
      window.removeEventListener('scroll', updateView);
    };
  }, []);

  useEffect(
    function watchDeviceOrientationChange() {
      const mediaQuery = min1024pxWideMediaQuery();
      handleDeviceOrientation();

      // Check if addEventListener is supported (modern browsers)
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleDeviceOrientation);
      } else {
        // Fallback for Safari 13 and older browsers
        mediaQuery.addListener(handleDeviceOrientation);
      }

      return function unwatchDeviceOrientationChange() {
        // Clean up with the same method
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleDeviceOrientation);
        } else {
          mediaQuery.removeListener(handleDeviceOrientation);
        }
      };
    },
    [handleDeviceOrientation]
  );

  useEffect(
    function recordHeaderImpression() {
      if (shouldRecordImpression && monetateId) {
        recordImpressionByName(
          monetate.channel,
          features.monetateExperiences,
          'record-impression',
          monetateId
        );
      }
    },
    [
      shouldRecordImpression,
      monetate.channel,
      features.monetateExperiences,
      monetateId,
    ]
  );

  useEffect(function listenForMobileHeaderEvents() {
    window.addEventListener(
      'jl-events.browser.mobileHeaderCloseMenu',
      onMenuButtonClick
    );
    window.addEventListener(
      'jl-events.browser.mobileHeaderUnpeep',
      handleUnpeep
    );

    return function stopListeningForMobileHeaderEvents() {
      window.removeEventListener(
        'jl-events.browser.mobileHeaderCloseMenu',
        onMenuButtonClick
      );
      window.removeEventListener(
        'jl-events.browser.mobileHeaderUnpeep',
        handleUnpeep
      );
    };
  }, []);

  const dispatchPeepEvent = useCallback(peeped => {
    const event = new CustomEvent('jl-events.browser.mobileHeaderPeepUpdated', {
      detail: { peeped },
    });

    window.dispatchEvent(event);
  }, []);

  const dispatchStickyHeaderEvent = useCallback(peeped => {
    const event = new CustomEvent('jl-events.browser.stickyHeaderPeepUpdated', {
      detail: { peeped },
    });

    window.dispatchEvent(event);
  }, []);

  const handleOverlayKeyPress = useCallback(
    event => {
      if (event.key === 'Enter') {
        onMenuButtonClick(event);
      }
    },
    [onMenuButtonClick]
  );

  const searchInputFocus = () => {
    window.scrollTo(0, 0);
    setState(prevState => ({ ...prevState, isFocused: true }));
    isFocusedRef.current = true;
    window.addEventListener('keydown', escKeyHandler);

    // Old styling - only apply to desktop
    if (isMin1024pxWide()) {
      document.body.classList.add(styles.typeaheadOpen);
      document.body.style.paddingRight = `${scrollbarWidthRef.current}px`;
    }

    if (isMin768pxWide() && hasStickyHeader && stickyHeaderActiveRef.current) {
      sendEvent('sticky-search-header.search-focussed');
    }

    recordImpressionByName(
      monetate.channel,
      features.monetateExperiences,
      'typeahead-content',
      monetateId
    );

    recordImpressionByName(
      monetate.channel,
      features.monetateExperiences,
      'typeahead-recs',
      monetateId
    );

    recordImpressionByName(
      monetate.channel,
      features.monetateExperiences,
      'typeahead-white',
      monetateId
    );

    dispatchNewEvent('dataStateChange', {
      action: 'global.searchInputBoxFocus',
    });

    if (showNavRef.current) {
      updateTypeaheadState(true);
    }
  };

  const wrapperClasses = classNames(styles.header, {
    [styles.navOpenPersistent]: state.showNav,
    [styles.isStickyHeaderActive]: state.stickyHeaderActive,
  });

  const overlayClasses = classNames(styles.overlay, {
    [styles.showOverlay]: state.showNav,
  });

  const splashClasses = state.isFocused
    ? classNames(styles.expandedSearch)
    : classNames(styles.search);

  const containerClasses = classNames(styles.container, {
    [styles.peepHeader]: features.peepHeader,
    [styles.peep]: features.peepHeader && !state.isVisible && !state.showNav,
  });

  const typeaheadSeparatorClasses = classNames(styles.typeaheadSeparator, {
    [styles.typeaheadSeparator__hidden]: !state.isFocused,
    [styles.typeaheadSeparator__hidden__doTransition]: !state.isFocused,
  });

  return (
    <div
      className={wrapperClasses}
      ref={headerContainer}
      data-testid="header-wrapper"
    >
      <div
        className={containerClasses}
        ref={header}
        data-testid="header-container"
      >
        <Prenav
          isFocused={state.isFocused}
          isStickyHeaderActive={state.stickyHeaderActive}
          onFocus={state.isFocused ? searchInputBlur : null}
          topLinksData={topLinksData}
        />
        <div
          className={classNames(styles.logoRow, {
            [styles.logoRow__active]: state.isFocused,
          })}
          data-testid="header-logo-row"
        >
          <Logo splashScreen={state.isFocused} />
          <div
            className={splashClasses}
            id="mobile-search"
            data-testid="mobile-search"
          >
            <Search
              hideSearch={
                features.peepHeader && !state.isVisible && !state.isFocused
              }
              id="mobileSearch"
              onFocus={searchInputFocus}
              isTypeaheadOpen={state.isFocused}
              onCloseButtonClick={searchInputBlur}
              onTabOut={onSearchTabOut}
            />
          </div>

          <div className={styles.buttonContainer}>
            <div className={styles.overlayButtons}>
              <ul>
                <li
                  className={classNames(styles.backButton, {
                    [styles.visible]: state.isFocused && state.showNav,
                  })}
                >
                  <BackButton
                    onBackButtonClick={() => {
                      setState(prevState => ({ ...prevState, showNav: true }));
                      updateMobileNavState(true);
                      updateTypeaheadState(false);
                      searchInputBlur();
                    }}
                  />
                </li>
                <li>
                  <CloseButton
                    isTypeaheadOpen={state.isFocused}
                    onCloseButtonClick={() => {
                      searchInputBlur();
                      updateTypeaheadState(false);
                      onMenuButtonClick(null, true);
                    }}
                    isDesktopCloseButton={false}
                  />
                </li>
              </ul>
            </div>

            <UserTools
              isMenuOpen={state.showNav}
              onMenuButtonClick={e => onMenuButtonClick(e)}
              isTypeaheadOpen={state.isFocused}
            />
          </div>
        </div>
        <TypeaheadOverlay
          isTypeaheadOpen={state.isFocused}
          onClickAway={searchInputBlur}
          isWhiteTypeaheadActive={isWhiteTypeaheadActive}
        />
        <hr
          data-testid="typeahead-separator"
          className={typeaheadSeparatorClasses}
        />
      </div>
      {state.showNav && (
        <div
          className={overlayClasses}
          onClick={e => onMenuButtonClick(e)}
          role="button"
          tabIndex={0}
          onKeyPress={handleOverlayKeyPress}
        />
      )}
    </div>
  );
};
